import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, CalendarHeart, MapPin } from "lucide-react";
import { api, resolveMedia } from "../../lib/api";
import { inputCls, errMsg, CategorySelect, UploadPicker } from "./shared";

const EMPTY = { title: "", date: "", location: "", description: "", image: "", category: "", status: "upcoming" };

export default function EventManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/events").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/events/${editing}`, form);
        toast.success("कार्यक्रम अपडेट हुआ");
      } else {
        await api.post("/admin/events", form);
        toast.success("कार्यक्रम जोड़ा गया");
      }
      setForm(EMPTY);
      setEditing(null);
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("क्या आप यह कार्यक्रम हटाना चाहते हैं?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      setItems((p) => p.filter((i) => i.id !== id));
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <button data-testid="event-new" onClick={() => { setForm(EMPTY); setEditing(null); setShowForm(!showForm); }} className="mb-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">
        <Plus size={16} /> नया कार्यक्रम जोड़ें
      </button>

      {showForm && (
        <form data-testid="event-form" onSubmit={save} className="bg-white rounded-2xl border border-ngo-deep/10 p-6 mb-8 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">कार्यक्रम का नाम *</label>
            <input data-testid="event-title" required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">तारीख</label>
            <input data-testid="event-date" type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">स्थान</label>
            <input data-testid="event-location" className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="रायपुर, वाराणसी" />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">श्रेणी</label>
            <CategorySelect testid="event-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} allowEmpty />
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">स्थिति</label>
            <select data-testid="event-status" className={inputCls} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="upcoming">आगामी</option>
              <option value="completed">संपन्न</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">कार्यक्रम फोटो</label>
            <div className="flex gap-2 items-center">
              <input data-testid="event-image" className={inputCls} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="फोटो अपलोड करें या URL डालें" />
              <UploadPicker testid="event-image-upload" context="events" label="अपलोड" onUploaded={(url) => setForm((p) => ({ ...p, image: url }))} />
            </div>
          </div>
          <div className="sm:col-span-2">
            <label className="text-xs font-semibold text-ngo-ink/60 block mb-1.5">विवरण</label>
            <textarea data-testid="event-description" rows={3} className={inputCls} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="sm:col-span-2 flex gap-3">
            <button data-testid="event-save" className="px-7 py-3 rounded-full bg-ngo-deep text-white font-bold hover:bg-ngo-forest transition-colors duration-300">{editing ? "अपडेट करें" : "कार्यक्रम जोड़ें"}</button>
            <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="px-6 py-3 rounded-full border border-ngo-deep/20 text-ngo-deep text-sm font-semibold">रद्द करें</button>
          </div>
        </form>
      )}

      {items.length === 0 && <p data-testid="event-empty" className="text-center text-ngo-ink/50 py-14">अभी कोई कार्यक्रम नहीं है।</p>}
      <div data-testid="event-list" className="space-y-4">
        {items.map((ev) => (
          <div key={ev.id} className="bg-white rounded-2xl border border-ngo-deep/10 p-5 flex flex-wrap items-center gap-4">
            {ev.image ? (
              <img src={resolveMedia(ev.image)} alt={ev.title} className="w-20 h-14 object-cover rounded-lg" />
            ) : (
              <div className="w-20 h-14 rounded-lg bg-ngo-mint flex items-center justify-center text-ngo-deep"><CalendarHeart size={20} /></div>
            )}
            <div className="flex-1 min-w-[200px]">
              <p className="font-semibold text-ngo-ink">{ev.title}</p>
              <p className="text-xs text-ngo-ink/50 mt-1 flex flex-wrap items-center gap-2">
                {ev.status === "upcoming" ? <span className="text-ngo-deep font-bold">● आगामी</span> : <span className="text-ngo-ink/40 font-bold">● संपन्न</span>}
                {ev.date && <span>{new Date(ev.date).toLocaleDateString("hi-IN")}</span>}
                {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {ev.location}</span>}
              </p>
            </div>
            <div className="flex gap-1.5">
              <button data-testid={`event-edit-${ev.id}`} onClick={() => { setEditing(ev.id); setForm({ title: ev.title, date: ev.date, location: ev.location, description: ev.description, image: ev.image, category: ev.category, status: ev.status }); setShowForm(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="संपादित करें" className="w-9 h-9 rounded-full bg-ngo-mint text-ngo-deep flex items-center justify-center hover:bg-ngo-leaf/25 transition-colors duration-200"><Pencil size={15} /></button>
              <button data-testid={`event-delete-${ev.id}`} onClick={() => remove(ev.id)} aria-label="हटाएं" className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100 transition-colors duration-200"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
